import { __decorate } from 'tslib';
import * as i0 from '@angular/core';
import { forwardRef, EventEmitter, Directive, Output, Component, ViewEncapsulation, ChangeDetectionStrategy, HostBinding, Input, HostListener, NgModule } from '@angular/core';
import { untilDestroyed, UntilDestroy } from '@ngneat/until-destroy';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subject } from 'rxjs';
import { CommonModule } from '@angular/common';

/* eslint-disable @typescript-eslint/no-explicit-any */
const RADIO_GROUP_ACCESSOR = {
    provide: NG_VALUE_ACCESSOR,
    useExisting: forwardRef(() => GpuRadioGroupDirective),
    multi: true,
};
class GpuRadioGroupDirective {
    constructor() {
        // Classic emitter if you want to listen in template
        this.ngChange = new EventEmitter();
        this.valueChange = new Subject();
        this.disabledChange = new Subject();
        this.isDisabled = false;
        this.onChange = () => { };
        this.onTouched = () => { };
    }
    writeValue(value) {
        this.changeValue(value);
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouched = fn;
    }
    setDisabledState(isDisabled) {
        this.isDisabled = isDisabled;
        this.disabledChange.next();
    }
    optionChanged(value) {
        this.changeValue(value);
        this.onChange(value);
        this.onTouched();
    }
    changeValue(value) {
        this.value = value;
        this.valueChange.next();
        this.ngChange.emit(value);
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuRadioGroupDirective, deps: [], target: i0.ɵɵFactoryTarget.Directive }); }
    static { this.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "14.0.0", version: "17.1.3", type: GpuRadioGroupDirective, selector: "gpu-radio-group", outputs: { ngChange: "ngChange" }, providers: [RADIO_GROUP_ACCESSOR], ngImport: i0 }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuRadioGroupDirective, decorators: [{
            type: Directive,
            args: [{
                    // eslint-disable-next-line @angular-eslint/directive-selector
                    selector: 'gpu-radio-group',
                    providers: [RADIO_GROUP_ACCESSOR],
                }]
        }], propDecorators: { ngChange: [{
                type: Output
            }] } });

let GpuRadioButtonComponent = class GpuRadioButtonComponent {
    get disabled() {
        return this.radioButtonDisabled;
    }
    set disabled(value) {
        this.radioButtonDisabled = value;
        this.isDisabled = this.radioButtonDisabled || this.parent.isDisabled;
    }
    constructor(element, parent, cdRef) {
        this.element = element;
        this.parent = parent;
        this.cdRef = cdRef;
        this.radioButtonClass = true;
        this.isSelected = false;
        this.isDisabled = false;
        this.focused = false;
        this.tabIndex = 0;
        this.radioButtonDisabled = false;
        this.parent.valueChange.pipe(untilDestroyed(this)).subscribe(() => this.toggleSelected());
        this.parent.disabledChange.pipe(untilDestroyed(this)).subscribe(() => this.toggleDisabled());
    }
    onClick(event) {
        event.stopPropagation();
        if (this.isDisabled || this.parent.value === this.value) {
            return;
        }
        this.parent.optionChanged(this.value);
    }
    onFocus(focused) {
        this.focused = focused;
    }
    ngAfterViewInit() {
        // Avoid ExpressionChangedAfterItHasBeenCheckedError
        setTimeout(() => {
            this.toggleSelected();
            this.toggleDisabled();
        });
    }
    toggleSelected() {
        this.isSelected = this.parent.value === this.value;
        // Set native element checked property, to work with non-angular tools
        this.element.nativeElement.checked = this.isSelected;
        this.cdRef.markForCheck();
    }
    toggleDisabled() {
        this.isDisabled = this.radioButtonDisabled || this.parent.isDisabled;
        this.cdRef.markForCheck();
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuRadioButtonComponent, deps: [{ token: i0.ElementRef }, { token: GpuRadioGroupDirective }, { token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "17.1.3", type: GpuRadioButtonComponent, selector: "gpu-radio-button", inputs: { focused: "focused", tabIndex: "tabIndex", id: "id", name: "name", value: "value", disabled: "disabled" }, host: { listeners: { "click": "onClick($event)", "focus": "onFocus(true)", "blur": "onFocus(false)" }, properties: { "class.gpu-radio-button": "this.radioButtonClass", "class.gpu-selected": "this.isSelected", "class.gpu-disabled": "this.isDisabled", "class.gpu-focused": "this.focused", "tabIndex": "this.tabIndex" } }, ngImport: i0, template: "<label [attr.for]=\"id\" class=\"gpu-radio-label\" #label>\n  <span class=\"gpu-radio-container\">\n    <span class=\"gpu-radio-inner-circle\"></span>\n    <span class=\"gpu-radio-outer-circle\"></span>\n    <span class=\"gpu-radio-focus-circle\"></span>\n    <input\n      #input\n      class=\"gpu-radio-input\"\n      type=\"radio\"\n      [id]=\"id\"\n      [checked]=\"isSelected\"\n      [disabled]=\"isDisabled\"\n      [attr.value]=\"value\"\n      [attr.name]=\"name\"\n      tabindex=\"-1\"\n    />\n  </span>\n\n  <span class=\"gpu-radio-label-content p3-regular\" [class.text-muted]=\"isDisabled\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: [".gpu-radio-button{display:inline-block;outline:0}.gpu-radio-button .gpu-radio-label{width:100%;display:inline-flex;align-items:center;vertical-align:middle;cursor:pointer;margin-bottom:0}.gpu-radio-button .gpu-radio-container{width:1.5rem;height:1.5rem;display:inline-block;position:relative;box-sizing:border-box;flex-shrink:0}.gpu-radio-button .gpu-radio-inner-circle,.gpu-radio-button .gpu-radio-outer-circle{height:1.5rem;width:1.5rem;display:block;position:absolute;top:0;left:0;border-radius:50%;box-sizing:border-box}.gpu-radio-button .gpu-radio-inner-circle{transform:scale(.5)}.gpu-radio-button .gpu-radio-outer-circle{border:2px solid #c7c7d2}.gpu-radio-button .gpu-radio-focus-circle{height:0px;width:0px;display:block;position:absolute;top:.75rem;left:.75rem;transition:all .3s ease-in-out;opacity:0;border-radius:50%;box-sizing:border-box}.gpu-radio-button .gpu-radio-input{visibility:hidden}.gpu-radio-button .gpu-radio-label-content{padding-left:1rem}.gpu-radio-button.gpu-focused .gpu-radio-focus-circle,.gpu-radio-button:hover .gpu-radio-focus-circle{height:2.5rem;width:2.5rem;top:-.5rem;left:-.5rem;opacity:1;background:#c7c7d21a}.gpu-radio-button.gpu-selected .gpu-radio-inner-circle{background:#6a81f6}.gpu-radio-button.gpu-selected .gpu-radio-outer-circle{border:2px solid #6a81f6}.gpu-radio-button.gpu-selected .gpu-radio-focus-circle{background:#6a81f61a}.gpu-radio-button.gpu-disabled .gpu-radio-label{cursor:default}.gpu-radio-button.gpu-disabled.gpu-selected .gpu-radio-inner-circle{background:#e5e5ea}.gpu-radio-button.gpu-disabled .gpu-radio-outer-circle{border:2px solid #e5e5ea}.gpu-radio-button.gpu-disabled .gpu-radio-focus-circle{background:none}\n"], changeDetection: i0.ChangeDetectionStrategy.OnPush, encapsulation: i0.ViewEncapsulation.None }); }
};
GpuRadioButtonComponent = __decorate([
    UntilDestroy()
], GpuRadioButtonComponent);
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuRadioButtonComponent, decorators: [{
            type: Component,
            args: [{ selector: 'gpu-radio-button', encapsulation: ViewEncapsulation.None, changeDetection: ChangeDetectionStrategy.OnPush, template: "<label [attr.for]=\"id\" class=\"gpu-radio-label\" #label>\n  <span class=\"gpu-radio-container\">\n    <span class=\"gpu-radio-inner-circle\"></span>\n    <span class=\"gpu-radio-outer-circle\"></span>\n    <span class=\"gpu-radio-focus-circle\"></span>\n    <input\n      #input\n      class=\"gpu-radio-input\"\n      type=\"radio\"\n      [id]=\"id\"\n      [checked]=\"isSelected\"\n      [disabled]=\"isDisabled\"\n      [attr.value]=\"value\"\n      [attr.name]=\"name\"\n      tabindex=\"-1\"\n    />\n  </span>\n\n  <span class=\"gpu-radio-label-content p3-regular\" [class.text-muted]=\"isDisabled\">\n    <ng-content></ng-content>\n  </span>\n</label>\n", styles: [".gpu-radio-button{display:inline-block;outline:0}.gpu-radio-button .gpu-radio-label{width:100%;display:inline-flex;align-items:center;vertical-align:middle;cursor:pointer;margin-bottom:0}.gpu-radio-button .gpu-radio-container{width:1.5rem;height:1.5rem;display:inline-block;position:relative;box-sizing:border-box;flex-shrink:0}.gpu-radio-button .gpu-radio-inner-circle,.gpu-radio-button .gpu-radio-outer-circle{height:1.5rem;width:1.5rem;display:block;position:absolute;top:0;left:0;border-radius:50%;box-sizing:border-box}.gpu-radio-button .gpu-radio-inner-circle{transform:scale(.5)}.gpu-radio-button .gpu-radio-outer-circle{border:2px solid #c7c7d2}.gpu-radio-button .gpu-radio-focus-circle{height:0px;width:0px;display:block;position:absolute;top:.75rem;left:.75rem;transition:all .3s ease-in-out;opacity:0;border-radius:50%;box-sizing:border-box}.gpu-radio-button .gpu-radio-input{visibility:hidden}.gpu-radio-button .gpu-radio-label-content{padding-left:1rem}.gpu-radio-button.gpu-focused .gpu-radio-focus-circle,.gpu-radio-button:hover .gpu-radio-focus-circle{height:2.5rem;width:2.5rem;top:-.5rem;left:-.5rem;opacity:1;background:#c7c7d21a}.gpu-radio-button.gpu-selected .gpu-radio-inner-circle{background:#6a81f6}.gpu-radio-button.gpu-selected .gpu-radio-outer-circle{border:2px solid #6a81f6}.gpu-radio-button.gpu-selected .gpu-radio-focus-circle{background:#6a81f61a}.gpu-radio-button.gpu-disabled .gpu-radio-label{cursor:default}.gpu-radio-button.gpu-disabled.gpu-selected .gpu-radio-inner-circle{background:#e5e5ea}.gpu-radio-button.gpu-disabled .gpu-radio-outer-circle{border:2px solid #e5e5ea}.gpu-radio-button.gpu-disabled .gpu-radio-focus-circle{background:none}\n"] }]
        }], ctorParameters: () => [{ type: i0.ElementRef }, { type: GpuRadioGroupDirective }, { type: i0.ChangeDetectorRef }], propDecorators: { radioButtonClass: [{
                type: HostBinding,
                args: ['class.gpu-radio-button']
            }], isSelected: [{
                type: HostBinding,
                args: ['class.gpu-selected']
            }], isDisabled: [{
                type: HostBinding,
                args: ['class.gpu-disabled']
            }], focused: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['class.gpu-focused']
            }], tabIndex: [{
                type: Input
            }, {
                type: HostBinding,
                args: ['tabIndex']
            }], id: [{
                type: Input
            }], name: [{
                type: Input
            }], value: [{
                type: Input
            }], disabled: [{
                type: Input
            }], onClick: [{
                type: HostListener,
                args: ['click', ['$event']]
            }], onFocus: [{
                type: HostListener,
                args: ['focus', ['true']]
            }, {
                type: HostListener,
                args: ['blur', ['false']]
            }] } });

class GpuRadioGroupModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuRadioGroupModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "17.1.3", ngImport: i0, type: GpuRadioGroupModule, declarations: [GpuRadioButtonComponent, GpuRadioGroupDirective], imports: [CommonModule], exports: [GpuRadioButtonComponent, GpuRadioGroupDirective] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuRadioGroupModule, imports: [CommonModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "17.1.3", ngImport: i0, type: GpuRadioGroupModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [GpuRadioButtonComponent, GpuRadioGroupDirective],
                    imports: [CommonModule],
                    exports: [GpuRadioButtonComponent, GpuRadioGroupDirective],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { GpuRadioButtonComponent, GpuRadioGroupDirective, GpuRadioGroupModule, RADIO_GROUP_ACCESSOR };
